import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/cn'
import { useAthletes, useActiveAssignments } from '@/hooks/useAthletes'
import { useAssignToMany, type AssignResult } from '@/hooks/usePrograms'
import type { Program } from '@/types'

/**
 * Modal: pick one or more athletes and give each their own copy of the program.
 * `preselectId` pre-ticks an athlete (e.g. "New program" from an athlete page).
 */
export function AssignDialog({
  program,
  onClose,
  preselectId,
  onAssigned,
}: {
  program: Program
  onClose: () => void
  preselectId?: string
  /** Called once at least one athlete was assigned (e.g. to clear a builder draft). */
  onAssigned?: () => void
}) {
  const navigate = useNavigate()
  const { data: athletes = [] } = useAthletes(true)
  const { data: current = {} } = useActiveAssignments(true)
  const assign = useAssignToMany()
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<Set<string>>(() => new Set(preselectId ? [preselectId] : []))
  const [progress, setProgress] = useState(0)
  const [results, setResults] = useState<AssignResult[] | null>(null)

  // Preselected athlete may load after first render.
  useEffect(() => {
    if (preselectId) setPicked((p) => (p.has(preselectId) ? p : new Set(p).add(preselectId)))
  }, [preselectId])

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? athletes.filter((a) => a.name.toLowerCase().includes(q)) : athletes
  }, [query, athletes])

  const toggle = (id: string) =>
    setPicked((p) => {
      const next = new Set(p)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const allShownPicked = shown.length > 0 && shown.every((a) => picked.has(a.id))
  const toggleAllShown = () =>
    setPicked((p) => {
      const next = new Set(p)
      if (allShownPicked) shown.forEach((a) => next.delete(a.id))
      else shown.forEach((a) => next.add(a.id))
      return next
    })

  const selected = athletes.filter((a) => picked.has(a.id))
  const replacing = selected.filter((a) => current[a.id])

  const confirm = () => {
    if (!selected.length) return
    setProgress(0)
    assign.mutate(
      { program, athletes: selected.map((a) => ({ id: a.id, name: a.name })), onProgress: setProgress },
      {
        onSuccess: (res) => {
          if (res.some((r) => r.ok)) onAssigned?.()
          // A single successful assignment goes straight to that athlete, as before.
          if (res.length === 1 && res[0].ok) navigate(`/athlete/${res[0].athleteId}`)
          else setResults(res)
        },
      }
    )
  }

  // ── Result summary ──
  if (results) {
    const ok = results.filter((r) => r.ok)
    const failed = results.filter((r) => !r.ok)
    return (
      <Shell title="Assign Program" subtitle={program.name} onClose={onClose}>
        <div className="p-5">
          {ok.length > 0 && (
            <p className="rounded-btn border border-success/35 bg-success/10 px-4 py-3 text-sm text-success">
              ✓ Assigned to {ok.length} athlete{ok.length === 1 ? '' : 's'}.
            </p>
          )}
          {failed.length > 0 && (
            <div className="mt-3 rounded-btn border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger">
              <p className="font-semibold">
                {failed.length} couldn’t be assigned — their existing program is unchanged:
              </p>
              <ul className="mt-1 list-disc pl-5 text-xs">
                {failed.map((f) => (
                  <li key={f.athleteId}>{f.name}: {f.error}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-5 flex gap-2">
            {failed.length > 0 && (
              <Button
                variant="secondary"
                onClick={() => {
                  setPicked(new Set(failed.map((f) => f.athleteId)))
                  setResults(null)
                }}
              >
                Retry failed
              </Button>
            )}
            <Button className="flex-1" onClick={onClose}>Done</Button>
          </div>
        </div>
      </Shell>
    )
  }

  return (
    <Shell title="Assign Program" subtitle={program.name} onClose={assign.isPending ? undefined : onClose}>
      <div className="p-5">
        <div className="flex items-center gap-2">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search athletes…"
            className="flex-1"
          />
          <button
            onClick={toggleAllShown}
            disabled={!shown.length}
            className="flex-shrink-0 font-condensed text-[11px] uppercase tracking-[0.12em] text-muted hover:text-gold disabled:opacity-30"
          >
            {allShownPicked ? 'Clear shown' : query ? 'Select shown' : 'Select all'}
          </button>
        </div>

        <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-border">
          {shown.length === 0 ? (
            <p className="px-3 py-3 text-sm italic text-muted">No athletes found</p>
          ) : (
            shown.map((a) => {
              const on = picked.has(a.id)
              const cur = current[a.id]
              return (
                <label
                  key={a.id}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 border-b border-border px-3 py-2 last:border-0 hover:bg-surface-2',
                    on && 'bg-gold/[.06]'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(a.id)}
                    className="h-4 w-4 flex-shrink-0 accent-gold"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-condensed text-base font-bold leading-tight">{a.name}</span>
                    <span className="block truncate text-[11px] text-subtle">
                      {cur ? `on ${cur.programName} · wk ${cur.currentWeek}` : 'no program'}
                    </span>
                  </span>
                </label>
              )
            })
          )}
        </div>

        <p className="mt-2 text-xs text-muted">
          {selected.length === 0
            ? 'Tick one or more athletes.'
            : `${selected.length} selected${selected.length <= 4 ? ': ' + selected.map((a) => a.name).join(', ') : ''}`}
        </p>

        {replacing.length > 0 && (
          <p className="mt-2 rounded-btn border border-gold/35 bg-gold/10 px-3 py-2 text-xs text-gold">
            {replacing.length} of these already {replacing.length === 1 ? 'has a program' : 'have programs'}, which
            will be replaced. Their logged weights and history are kept.
          </p>
        )}

        {assign.isError && (
          <p className="mt-3 text-sm text-danger">{(assign.error as Error).message}</p>
        )}

        <div className="mt-5 flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={assign.isPending}>
            Cancel
          </Button>
          <Button className="flex-1" disabled={!selected.length || assign.isPending} onClick={confirm}>
            {assign.isPending
              ? `Assigning ${Math.min(progress + 1, selected.length)} of ${selected.length}…`
              : selected.length > 1
              ? `Assign to ${selected.length} athletes`
              : 'Save & Assign'}
          </Button>
        </div>
      </div>
    </Shell>
  )
}

function Shell({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string
  subtitle: string
  onClose?: () => void
  children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-card border border-border bg-surface shadow-card-hover animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-display text-2xl tracking-wide">{title}</h2>
          <p className="mt-0.5 truncate text-sm text-muted">{subtitle}</p>
        </div>
        {children}
      </div>
    </div>
  )
}
